import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, retry } from "rxjs/operators";
import { environment } from "../../environments/environment";

export interface ApiResponse<T> {
  data: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface SearchParams {
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  [key: string]: any;
}

@Injectable({
  providedIn: "root",
})
export class ApiService {
  private baseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) { }

  // Generic CRUD operations
  get<T>(endpoint: string, params?: SearchParams): Observable<ApiResponse<T>> {
    let httpParams = new HttpParams();

    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key].toString());
        }
      });
    }

    // Add cache-busting parameter to prevent caching issues
    httpParams = httpParams.set("_t", Date.now().toString());

    const httpOptions = {
      params: httpParams,
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
    };

    return this.http
      .get<ApiResponse<T>>(`${this.baseUrl}/${endpoint}`, httpOptions)
      .pipe(retry(1), catchError(this.handleError));
  }

  getById<T>(endpoint: string, id: number): Observable<T> {
    return this.http
      .get<T>(`${this.baseUrl}/${endpoint}/${id}`)
      .pipe(retry(1), catchError(this.handleError));
  }

  create<T>(endpoint: string, data: any): Observable<T> {
    return this.http
      .post<T>(`${this.baseUrl}/${endpoint}`, data)
      .pipe(catchError(this.handleError));
  }

  update<T>(endpoint: string, id: number, data: any): Observable<T> {
    return this.http
      .put<T>(`${this.baseUrl}/${endpoint}/${id}`, data)
      .pipe(catchError(this.handleError));
  }

  delete(endpoint: string, id: number): Observable<any> {
    return this.http
      .delete(`${this.baseUrl}/${endpoint}/${id}`)
      .pipe(catchError(this.handleError));
  }

  // Search operations
  search<T>(
    endpoint: string,
    searchTerm: string,
    params?: SearchParams
  ): Observable<ApiResponse<T>> {
    const searchParams = { ...params, searchTerm };
    return this.get<T>(`${endpoint}/search`, searchParams);
  }

  // Status-specific operations
  getByStatus<T>(
    endpoint: string,
    status: string,
    params?: SearchParams
  ): Observable<ApiResponse<T>> {
    const statusParams = { ...params };
    return this.get<T>(`${endpoint}/status/${status}`, statusParams);
  }

  // Lookup operations (lightweight id + label lists for dropdowns)
  getLookup<T>(endpoint: string): Observable<T[]> {
    return this.http
      .get<T[]>(`${this.baseUrl}/${endpoint}/lookup`)
      .pipe(retry(1), catchError(this.handleError));
  }

  // Summary operations
  getSummary<T>(endpoint: string): Observable<T> {
    return this.http
      .get<T>(`${this.baseUrl}/${endpoint}/summary`)
      .pipe(retry(1), catchError(this.handleError));
  }

  // Specialized endpoints
  getUrgentReschedules(): Observable<any> {
    return this.http
      .get(`${this.baseUrl}/reschedules/urgent`)
      .pipe(retry(1), catchError(this.handleError));
  }

  getPaymentSummary(body?: { fromDate?: string; toDate?: string }): Observable<any> {
    return this.http
      .post(`${this.baseUrl}/payments/summary`, body ?? {})
      .pipe(catchError(this.handleError));
  }

  getActivePaymentMethods(): Observable<any> {
    return this.http
      .get(`${this.baseUrl}/paymentmethods/active`)
      .pipe(retry(1), catchError(this.handleError));
  }

  // Dashboard KPI cards and chart data
  getDashboardKpis(): Observable<any> {
    return this.http
      .get(`${this.baseUrl}/dashboard/kpis`)
      .pipe(retry(1), catchError(this.handleError));
  }

  // Unified dashboard income (financial-year or past-12-months tab)
  getDashboardIncome(
    tab: "financial-year" | "past-12-months",
    options?: {
      year?: number;
      fromDate?: string;
      toDate?: string;
    }
  ): Observable<any> {
    let httpParams = new HttpParams().set("tab", tab);
    if (options?.year) {
      httpParams = httpParams.set("year", options.year.toString());
    }
    if (options?.fromDate) {
      httpParams = httpParams.set("fromDate", options.fromDate);
    }
    if (options?.toDate) {
      httpParams = httpParams.set("toDate", options.toDate);
    }
    return this.http
      .get(`${this.baseUrl}/dashboard/income`, { params: httpParams })
      .pipe(retry(1), catchError(this.handleError));
  }

  /** POST to a path relative to baseUrl (no retry — safe for non-idempotent requests). */
  postPath<T>(
    path: string,
    body?: unknown,
    options?: {
      params?: HttpParams | Record<string, string>;
      headers?: HttpHeaders | Record<string, string>;
    },
  ): Observable<T> {
    return this.http
      .post<T>(this.buildUrl(path), body ?? {}, this.toRequestOptions(options))
      .pipe(catchError(this.handleError));
  }

  /** PUT to a path relative to baseUrl (no retry). */
  putPath<T>(
    path: string,
    body?: unknown,
    options?: {
      params?: HttpParams | Record<string, string>;
      headers?: HttpHeaders | Record<string, string>;
    },
  ): Observable<T> {
    return this.http
      .put<T>(this.buildUrl(path), body ?? {}, this.toRequestOptions(options))
      .pipe(catchError(this.handleError));
  }

  /** GET from a path relative to baseUrl (retries once by default). */
  getPath<T>(
    path: string,
    options?: {
      params?: HttpParams | Record<string, string>;
      retryRequest?: boolean;
      headers?: HttpHeaders | Record<string, string>;
    },
  ): Observable<T> {
    let request$ = this.http.get<T>(
      this.buildUrl(path),
      this.toRequestOptions(options),
    );
    if (options?.retryRequest !== false) {
      request$ = request$.pipe(retry(1));
    }
    return request$.pipe(catchError(this.handleError));
  }

  /** GET blob from a path relative to baseUrl. */
  getBlobPath(
    path: string,
    options?: { params?: HttpParams | Record<string, string> },
  ): Observable<Blob> {
    return this.http
      .get(this.buildUrl(path), {
        ...this.toRequestOptions(options),
        responseType: "blob",
      })
      .pipe(catchError(this.handleError));
  }

  /** POST blob from a path relative to baseUrl. */
  postBlobPath(
    path: string,
    body?: unknown,
    options?: { params?: HttpParams | Record<string, string> },
  ): Observable<Blob> {
    return this.http
      .post(this.buildUrl(path), body ?? {}, {
        ...this.toRequestOptions(options),
        responseType: "blob",
      })
      .pipe(catchError(this.handleError));
  }

  /** GET with full HttpResponse (e.g. response headers). */
  getPathWithResponse<T>(
    path: string,
    options?: {
      params?: HttpParams | Record<string, string>;
      retryRequest?: boolean;
    },
  ): Observable<HttpResponse<T>> {
    let request$ = this.http.get<T>(this.buildUrl(path), {
      ...this.toRequestOptions(options),
      observe: "response",
    });
    if (options?.retryRequest !== false) {
      request$ = request$.pipe(retry(1));
    }
    return request$.pipe(catchError(this.handleError));
  }

  /** DELETE from a path relative to baseUrl (no retry). */
  deletePath<T>(
    path: string,
    options?: { headers?: HttpHeaders | Record<string, string> },
  ): Observable<T> {
    return this.http
      .delete<T>(this.buildUrl(path), this.toRequestOptions(options))
      .pipe(catchError(this.handleError));
  }

  resolveUrl(path: string): string {
    return this.buildUrl(path);
  }

  private buildUrl(path: string): string {
    const normalized = path.replace(/^\/+/, "");
    return `${this.baseUrl}/${normalized}`;
  }

  private toRequestOptions(options?: {
    params?: HttpParams | Record<string, string>;
    headers?: HttpHeaders | Record<string, string>;
  }):
    | {
        params?: HttpParams;
        headers?: HttpHeaders;
      }
    | undefined {
    if (!options) {
      return undefined;
    }

    const requestOptions: {
      params?: HttpParams;
      headers?: HttpHeaders;
    } = {};

    const params = this.toHttpParams(options.params);
    if (params) {
      requestOptions.params = params;
    }

    const headers = this.toHttpHeaders(options.headers);
    if (headers) {
      requestOptions.headers = headers;
    }

    return Object.keys(requestOptions).length ? requestOptions : undefined;
  }

  private toHttpParams(
    params?: HttpParams | Record<string, string>,
  ): HttpParams | undefined {
    if (!params) {
      return undefined;
    }
    if (params instanceof HttpParams) {
      return params;
    }
    let httpParams = new HttpParams();
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null) {
        httpParams = httpParams.set(key, String(value));
      }
    });
    return httpParams;
  }

  private toHttpHeaders(
    headers?: HttpHeaders | Record<string, string>,
  ): HttpHeaders | undefined {
    if (!headers) {
      return undefined;
    }
    if (headers instanceof HttpHeaders) {
      return headers;
    }
    let httpHeaders = new HttpHeaders();
    Object.keys(headers).forEach((key) => {
      const value = headers[key];
      if (value !== undefined && value !== null) {
        httpHeaders = httpHeaders.set(key, String(value));
      }
    });
    return httpHeaders;
  }

  /** @deprecated Use toRequestOptions via getPath/postPath helpers. */
  private toHttpOptions(
    params?: HttpParams | Record<string, string>,
  ): { params: HttpParams } | undefined {
    const httpParams = this.toHttpParams(params);
    return httpParams ? { params: httpParams } : undefined;
  }

  // Error handling
  private handleError(error: any) {
    // Preserve the original HttpErrorResponse so callers
    // can inspect status and backend error payload.
    console.error("API error:", error);
    return throwError(() => error);
  }
}
